import type { FlagStep } from 'definitions';
import type { FlagState } from './flag-types';
import { FLAG_FULL_ROLLOUT_BP } from 'definitions';
// The server's own rule, so what the confirm says is what resume-flag.ts does. Pure: it imports
// nothing but a type.
import { stepToStartFrom } from '@@/api/flags/plan-steps';
import { isGuardrailPause } from './flag-guardrail';

/**
 * What a flag is doing, as the dashboard names it. Stored as an experiment status plus the
 * rollout row (design doc section 1); this is the one place that maps the two to a word.
 * 'Partly on' is a live flag held at a share by hand, with no plan moving it.
 */
export const FLAG_STATUS_LABELS = [
  'Draft',
  'Rolling out',
  'Paused',
  'Partly on',
  'Fully on',
  'Off',
  'Archived',
] as const;
export type FlagStatusLabel = (typeof FLAG_STATUS_LABELS)[number];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** 2500 basis points as "25%", 1250 as "12.5%". */
export const percentOf = (bp: number) => `${Number((bp / 100).toFixed(2))}%`;

/**
 * The status label. An active plan wins over the share it is at (a plan may start at 0%), and
 * the kill switch wins over a paused plan: a flag turned off reads Off, not Paused.
 */
export function flagStatus(flag: FlagState): FlagStatusLabel {
  if (flag.status === 'Draft') return 'Draft';
  if (flag.status !== 'Running') return 'Archived';
  const rollout = flag.flagRollout;
  if (!rollout) return 'Off';
  if (rollout.planStatus === 'active') return 'Rolling out';
  if (rollout.rolloutBp === 0) return 'Off';
  if (rollout.planStatus === 'paused') return 'Paused';
  if (rollout.rolloutBp >= FLAG_FULL_ROLLOUT_BP) return 'Fully on';
  return 'Partly on';
}

/** "step 2 of 4" while a plan is rolling out or paused on a step, else null. */
export function stepProgress(flag: FlagState): string | null {
  const rollout = flag.flagRollout;
  if (!rollout || flag.status !== 'Running' || rollout.steps.length === 0) return null;
  if (rollout.planStatus !== 'active' && rollout.planStatus !== 'paused') return null;
  const index = Math.min(rollout.currentStep, rollout.steps.length - 1);
  return `step ${index + 1} of ${rollout.steps.length}`;
}

/** A wait as people say it: "35m", "18h", "2d 4h". Under a minute is "1m". */
export function formatWait(ms: number): string {
  if (ms >= DAY) {
    const days = Math.floor(ms / DAY);
    const hours = Math.floor((ms % DAY) / HOUR);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (ms >= HOUR) return `${Math.round(ms / HOUR)}h`;
  return `${Math.max(1, Math.round(ms / MINUTE))}m`;
}

/**
 * The earliest the current step may hand over to the next: its minimum time counted from when
 * it started, and its not-before date (FlagStep: both are what must hold before the plan moves
 * on). A 'kpi' step's minimum time does not gate it (decide-flag-action.ts), only its date;
 * a 'kpi' or 'both' step also waits for the guardrail, which no time can say.
 */
export function stepDueAt(step: FlagStep, stepStartedAt: number | null): number {
  const byDuration = step.gate === 'kpi' ? 0 : (stepStartedAt ?? 0) + (step.minDurationMs ?? 0);
  return Math.max(byDuration, step.notBefore ?? 0);
}

type NextStepParams = { flag: FlagState; now: number };

/**
 * The next thing that happens to the rollout, for the list's Next step column: "50% in 18h"
 * while a plan runs ("50% after the guardrail" on a step that waits on it), "Waiting for you"
 * when it is paused ("Guardrail breach" when the guardrail paused it), the share a draft starts
 * at. Null when nothing is coming.
 */
export function nextStep({ flag, now }: NextStepParams): string | null {
  const rollout = flag.flagRollout;
  if (!rollout) return null;

  if (flag.status === 'Draft') return `Starts at ${percentOf(publishStart(rollout).bp)}`;
  if (flag.status !== 'Running') return null;

  if (rollout.planStatus === 'paused') {
    return isGuardrailPause(rollout) ? 'Guardrail breach' : 'Waiting for you';
  }
  if (rollout.planStatus !== 'active') return null;

  const current = rollout.steps[rollout.currentStep];
  const next = rollout.steps[rollout.currentStep + 1];
  if (!current) return null;
  if (!next) return 'Last step';
  const wait = stepDueAt(current, rollout.stepStartedAt) - now;
  const share = percentOf(next.rolloutBp);
  if (current.gate !== 'time') {
    return wait > 0
      ? `${share} after the guardrail, ${formatWait(wait)} at the earliest`
      : `${share} after the guardrail`;
  }
  return wait > 0 ? `${share} in ${formatWait(wait)}` : `${share} shortly`;
}

type PublishRollout = Pick<NonNullable<FlagState['flagRollout']>, 'steps' | 'kind' | 'rolloutBp'>;

/**
 * Where a draft's rollout starts when it is published, as publish-flag.ts decides it: at the
 * plan's first step when there is a plan; an editor flag left at 0% for everyone; otherwise at
 * the rollout it was given.
 */
export type PublishStart = { from: 'plan' | 'editor-default' | 'rollout'; bp: number };

export function publishStart(rollout: PublishRollout): PublishStart {
  const [first] = rollout.steps;
  if (first) return { from: 'plan', bp: first.rolloutBp };
  if (rollout.kind === 'editor' && rollout.rolloutBp === 0) return { from: 'editor-default', bp: FLAG_FULL_ROLLOUT_BP };
  return { from: 'rollout', bp: rollout.rolloutBp };
}

/** What publishing does to this draft, for the Publish confirm (publish-flag.ts decides it). */
export function publishSummary(flag: FlagState): string {
  const rollout = flag.flagRollout;
  if (!rollout) return 'It goes live.';
  const start = publishStart(rollout);
  if (start.from === 'plan') return `It goes live at ${percentOf(start.bp)} and the plan starts.`;
  if (start.from === 'editor-default') return 'It goes live for everyone. Turn off takes it back.';
  if (start.bp === 0) return 'It goes live at 0%: nobody gets On until you raise the rollout.';
  return `It goes live at ${percentOf(start.bp)}.`;
}

/**
 * What Start plan or Resume does from the current rollout (resume-flag.ts): the step it picks
 * up at, the first that rolls out at least the current share, or 'done' when the rollout is
 * already past every step and the plan has nothing left to do.
 */
export type PlanStartPreview =
  | { kind: 'step'; index: number; total: number; fromBp: number; toBp: number }
  | { kind: 'done'; fromBp: number };

type PlanStartPreviewParams = { steps: readonly FlagStep[]; rolloutBp: number };

export function planStartPreview({ steps, rolloutBp }: PlanStartPreviewParams): PlanStartPreview {
  const index = stepToStartFrom(steps, rolloutBp);
  const step = index === null ? undefined : steps[index];
  if (index === null || !step) return { kind: 'done', fromBp: rolloutBp };
  return { kind: 'step', index, total: steps.length, fromBp: rolloutBp, toBp: step.rolloutBp };
}

/** Sort order for the status column: live first, then drafts, archived last. */
export const FLAG_STATUS_ORDER: Record<FlagStatusLabel, number> = {
  'Rolling out': 0,
  Paused: 1,
  'Partly on': 2,
  'Fully on': 3,
  Off: 4,
  Draft: 5,
  Archived: 6,
};

export type FlagStage = 'draft' | 'live' | 'archived';

export type FlagPlanState =
  | { kind: 'none' }
  | { kind: 'not-started' }
  | { kind: 'active' | 'paused'; step: number; total: number }
  | { kind: 'done' };

/** Why the plan is held, said once on the flag page's status card. */
export type FlagPauseNotice = { kind: 'guardrail' | 'paused'; reason: string | null } | null;

export type FlagSummary = {
  stage: FlagStage;
  /** The share that gets On now: the rollout, less any holdback above it (as rolloutFigure says). */
  onBp: number;
  plan: FlagPlanState;
  pause: FlagPauseNotice;
};

/**
 * What the status card under a flag's name says: its stage, the share that gets On, where its
 * plan is, and, when the plan is paused, why. A draft gets nobody On yet; where it starts is the
 * card's Next.
 */
export function flagSummary(flag: FlagState): FlagSummary {
  const rollout = flag.flagRollout;
  const stage: FlagStage = flag.status === 'Draft' ? 'draft' : flag.status === 'Running' ? 'live' : 'archived';
  const onBp = stage === 'live' && rollout ? Math.min(rollout.rolloutBp, FLAG_FULL_ROLLOUT_BP - rollout.holdbackBp) : 0;
  const total = rollout?.steps.length ?? 0;
  const step = Math.min(rollout?.currentStep ?? 0, Math.max(0, total - 1)) + 1;
  const status = rollout?.planStatus ?? 'none';

  const plan: FlagPlanState =
    total === 0
      ? { kind: 'none' }
      : stage !== 'live' || status === 'none'
        ? { kind: 'not-started' }
        : status === 'done'
          ? { kind: 'done' }
          : { kind: status, step, total };

  const pause: FlagPauseNotice =
    stage === 'live' && rollout && status === 'paused'
      ? { kind: isGuardrailPause(rollout) ? 'guardrail' : 'paused', reason: rollout.pauseReason ?? null }
      : null;

  return { stage, onBp: Math.max(0, onBp), plan, pause };
}
